import { useEffect, useState } from "react";
import PortalLayout from "@/layouts/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Send, Users, Tag, Building2, Mail, Loader2 } from "lucide-react";

const TARGETS = [
  { id: "subscribers", label: "Abonnés Weekly Digest (opt-in public)", icon: Mail },
  { id: "all_members", label: "Tous les membres actifs", icon: Users },
  { id: "category", label: "Par catégorie d'entreprise", icon: Tag },
  { id: "pole", label: "Par pôle sectoriel", icon: Building2 },
];

export default function AdminDigest() {
  const [targets, setTargets] = useState({ categories: [], poles: [] });
  const [target, setTarget] = useState("subscribers");
  const [value, setValue] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/admin/digest/targets")
      .then((r) => setTargets(r.data))
      .catch(() => {});
  }, []);

  const previewAudience = async () => {
    if ((target === "category" || target === "pole") && !value) {
      toast.error("Sélectionnez d'abord une valeur");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/admin/digest/preview", { params: { target, value } });
      setPreview(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    }
    setLoading(false);
  };

  const send = async () => {
    if (!preview || preview.count === 0) {
      toast.error("Aperçu de l'audience requis avant envoi");
      return;
    }
    if (!window.confirm(`Envoyer le Weekly Digest à ${preview.count} destinataire${preview.count > 1 ? "s" : ""} ?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/digest/send-targeted", { target, value });
      toast.success(`✅ ${data.sent} envoyé${data.sent > 1 ? "s" : ""} · ${data.skipped} ignoré${data.skipped > 1 ? "s" : ""}`);
      setPreview(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    }
    setSending(false);
  };

  const needsValue = target === "category" || target === "pole";
  const options = target === "category" ? targets.categories : target === "pole" ? targets.poles : [];

  return (
    <PortalLayout sidebar="admin">
      <div className="max-w-4xl">
        <div>
          <h1 className="text-3xl font-bold text-navy md:text-4xl">Weekly Digest</h1>
          <p className="mt-2 text-neutral-600">Envoyez la newsletter hebdomadaire à un public ciblé : tous les abonnés, tous les membres, ou un segment par catégorie / pôle sectoriel.</p>
        </div>

        <div className="mt-8 rounded-2xl border border-neutral-100 bg-white p-6">
          <div className="text-xs font-bold uppercase tracking-wider text-neutral-500">1. Choisir la cible</div>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {TARGETS.map((t) => {
              const Icon = t.icon;
              const active = target === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => { setTarget(t.id); setValue(""); setPreview(null); }}
                  className={`flex items-start gap-3 rounded-xl border-2 p-4 text-left transition-all ${
                    active ? "border-wine-500 bg-wine-50/50" : "border-neutral-200 bg-white hover:border-wine-200"
                  }`}
                  data-testid={`digest-target-${t.id}`}
                >
                  <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${active ? "text-wine-600" : "text-neutral-400"}`} />
                  <div className="text-sm font-semibold text-navy">{t.label}</div>
                </button>
              );
            })}
          </div>

          {needsValue && (
            <div className="mt-5">
              <label className="text-xs font-bold uppercase tracking-wider text-neutral-500">
                {target === "category" ? "Catégorie" : "Pôle sectoriel"}
              </label>
              <select
                value={value}
                onChange={(e) => { setValue(e.target.value); setPreview(null); }}
                className="mt-2 w-full rounded-xl border border-neutral-200 px-4 py-3 text-sm font-semibold text-navy"
                data-testid="digest-value-select"
              >
                <option value="">— Sélectionner —</option>
                {options.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-2">
            <button
              onClick={previewAudience}
              disabled={loading || (needsValue && !value)}
              className="inline-flex items-center gap-1.5 rounded-full border border-wine-300 bg-white px-5 py-2 text-xs font-bold text-wine-700 hover:bg-wine-50 disabled:opacity-50"
              data-testid="digest-preview-btn"
            >
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Users className="h-3.5 w-3.5" />}
              Prévisualiser l&apos;audience
            </button>
            {preview && (
              <button
                onClick={send}
                disabled={sending || preview.count === 0}
                className="inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-5 py-2 text-xs font-bold text-white hover:bg-wine-600 disabled:opacity-50"
                data-testid="digest-send-btn"
              >
                {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                Envoyer ({preview.count})
              </button>
            )}
          </div>

          {preview && (
            <div className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50/60 p-4" data-testid="digest-preview-result">
              <div className="text-sm font-bold text-emerald-800">
                {preview.count === 0 ? "❌ Aucun destinataire trouvé" : `📬 ${preview.count} destinataire${preview.count > 1 ? "s" : ""} cible${preview.count > 1 ? "s" : ""}`}
              </div>
              {preview.sample && preview.sample.length > 0 && (
                <div className="mt-2 text-xs text-neutral-600">
                  Aperçu : {preview.sample.join(", ")}{preview.count > preview.sample.length ? "…" : ""}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50/60 p-4 text-xs text-amber-900">
          <div className="font-bold">ℹ️ Contenu du digest</div>
          <p className="mt-1">Le digest agrège automatiquement : actualités publiées sur les 7 derniers jours, événements à venir sous 14 jours, et nouveaux membres de la semaine.</p>
        </div>
      </div>
    </PortalLayout>
  );
}
